<template>
  <Box rounded="xlarge" class="tw-relative tw-space-y-5 tw-bg-white/5">
    <div
      v-if="busyText"
      class="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-rounded-[inherit] tw-p-5 tw-text-center tw-backdrop-blur"
    >
      <Spinner size="medium" />

      <Text class="tw-font-black">{{ busyText }}</Text>
      <Text>{{ t('networking.wan_busy_check', { count: PING_TIMEOUT_SEC }) }}</Text>
    </div>

    <div class="tw-space-y-1">
      <Heading level="h4">{{ t('networking.wan_title') }}</Heading>
      <Text>{{ t('networking.wan_subtitle') }}</Text>
    </div>

    <Text v-if="enabledCount >= 2" class="tw-border-l-4 tw-border-orange tw-pl-3 tw-font-black tw-text-orange">
      {{ t('networking.wan_backup_warning') }}
    </Text>

    <WanInterfaceList
      :interfaces="interfaces"
      :order="pendingOrder || serverOrder"
      :disabled="Boolean(busyText)"
      @reorder="confirmReorder"
      @toggle="confirmToggle"
      @settings="wifiModalOpen = true"
    />

    <WanConfirmModal
      v-model="confirmOpen"
      :title="confirmState?.title"
      :lines="confirmState?.lines"
      :warning="t('networking.stream_drop_warning')"
      :accept-label="t('networking.wan_confirm_accept')"
      @accept="onConfirmAccept"
    />

    <WanWifiModal
      v-model="wifiModalOpen"
      :current-ssid="byType.wifi?.provider || ''"
      :current-password="byType.wifi?.password || ''"
      :enabled="Boolean(byType.wifi?.enabled)"
      :priority="priorityOf('wifi')"
      :timeout-sec="PING_TIMEOUT_SEC"
      @connect="connectWifi"
    />
  </Box>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Box, Heading, Spinner, Text } from '@slideslive/fuse-kit/vue';

import api from '@/api';
import WanConfirmModal from '@/components/WanConfirmModal.vue';
import WanInterfaceList from '@/components/WanInterfaceList.vue';
import WanWifiModal from '@/components/WanWifiModal.vue';
import fetcher from '@/fetcher';
import useStateStore from '@/stores/state';

const { t } = useI18n();

const stateStore = useStateStore();
const { updateRemoteState } = stateStore;
const { state } = storeToRefs(stateStore);

// The backend accepts 5–600 s. It keeps a change only if the station is online within this time.
const PING_TIMEOUT_SEC = 60;

// Upper bound for the /state refresh after a change, so a hung request can't keep the overlay up.
const REFRESH_WAIT_MS = 5000;

const wan = computed(() => state.value?.data?.wan || {});
const interfaces = computed(() => (Array.isArray(wan.value.failover?.interfaces) ? wan.value.failover.interfaces : []));
const byType = computed(() => Object.fromEntries(interfaces.value.map((iface) => [iface.type, iface])));

// Interfaces arrive sorted, but sort defensively.
const serverOrder = computed(() =>
  [...interfaces.value].sort((a, b) => Number(a.priority) - Number(b.priority)).map((iface) => iface.type),
);
const enabledCount = computed(() => interfaces.value.filter((iface) => iface.enabled).length);
const priorityOf = (type) => serverOrder.value.indexOf(type) + 1;
const label = (type) => t(`networking.iface_${type}`);

// The new order while its confirmation is open and while it is applied, so the row stays where it was dropped.
const pendingOrder = ref(null);
const busyText = ref('');
const wifiModalOpen = ref(false);

const refreshState = async () => {
  try {
    const res = await api.state();
    if (res) updateRemoteState(res);
  } catch {
    // Regular /state polling keeps retrying; nothing to do here.
  }
};

// Every change is one request. The router checks it and undoes it if the station goes offline,
// and the fetcher shows the result as a notification.
const apply = async (text, path, payload) => {
  if (busyText.value) return;
  busyText.value = text;

  try {
    await fetcher.post(path, payload);
  } catch {
    // The fetcher already shows the error notification.
  } finally {
    // Keep the overlay until fresh /state arrives, otherwise the list shows the old state until the next poll.
    await Promise.race([refreshState(), new Promise((resolve) => window.setTimeout(resolve, REFRESH_WAIT_MS))]);
    busyText.value = '';
    pendingOrder.value = null;
  }
};

const confirmOpen = ref(false);
const confirmState = ref(null);
let accepted = false;

const openConfirm = ({ title, lines, onAccept }) => {
  accepted = false;
  confirmState.value = { title, lines, onAccept };
  confirmOpen.value = true;
};

const onConfirmAccept = () => {
  accepted = true;
  confirmState.value?.onAccept?.();
};

// Cancel, Escape and a click outside only close the modal: drop the previewed order.
watch(confirmOpen, (open) => {
  if (!open && !accepted) pendingOrder.value = null;
});

const confirmToggle = (type, enabled) => {
  const name = label(type);

  if (enabled && type === 'wifi' && !byType.value.wifi?.provider) {
    // No network to rejoin yet: ask for one, the connect enables WiFi too.
    wifiModalOpen.value = true;
    return;
  }

  const lines = [];
  if (enabled) {
    lines.push(t('networking.wan_enable_line', { name, priority: priorityOf(type) }));
    if (type === 'wifi') lines.push(t('networking.wan_enable_wifi_line', { ssid: byType.value.wifi.provider }));
    if (type === 'cellular') lines.push(t('networking.wan_enable_cellular_line'));
  } else {
    lines.push(
      t(byType.value[type]?.active ? 'networking.wan_disable_active_line' : 'networking.wan_disable_line', { name }),
    );
  }
  lines.push(t('networking.wan_check_line', { count: PING_TIMEOUT_SEC }));

  openConfirm({
    title: t(enabled ? 'networking.wan_enable_title' : 'networking.wan_disable_title', { name }),
    lines,
    onAccept: () =>
      apply(t(enabled ? 'networking.wan_busy_enable' : 'networking.wan_busy_disable', { name }), '/wan/-/interface', {
        type,
        enabled,
        ping_timeout: PING_TIMEOUT_SEC,
      }),
  });
};

const confirmReorder = (order) => {
  pendingOrder.value = order;

  // The router uses the first enabled interface that has internet.
  const next = order.find((type) => byType.value[type]?.enabled && byType.value[type]?.online);
  const lines = [t('networking.wan_priorities_line', { order: order.map(label).join(' → ') })];
  if (next) lines.push(t('networking.wan_priorities_active_line', { name: label(next) }));
  lines.push(t('networking.wan_check_line', { count: PING_TIMEOUT_SEC }));

  openConfirm({
    title: t('networking.wan_priorities_title'),
    lines,
    onAccept: () =>
      apply(t('networking.wan_busy_priorities'), '/wan/-/priorities', { order, ping_timeout: PING_TIMEOUT_SEC }),
  });
};

// The modal is the confirmation. If the new network fails, the router returns to the previous one
// and the response is a warning.
const connectWifi = ({ ssid, password }) =>
  apply(t('networking.wan_busy_wifi', { ssid }), '/wan/-/configure', {
    type: 'wifi',
    ssid,
    password,
    revert_when_fails: true,
    ping_timeout: PING_TIMEOUT_SEC,
  });
</script>
